import { Button, Card, Chip, Divider, Link, Stack, styled, SxProps, Typography } from "@mui/material";
import * as React from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import NotificationActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useNavigate } from "react-router";
import { ingestService } from "../../../Core/Api/services";
import { AdminAssetSingleRequestDetails } from "../../../Core/Api/Types/db-asset-object-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ModalContext } from "../../Modals";
import ModalConfirmActionOpenButton from "../../Modals/ModalConfirmAction/modal-confirm-action-open-button";
import ModalNewAssignment, {
  ModalNewAssignmentElementProps,
} from "../../Modals/ModalNewAssignment/modal-new-assignment";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import { routePaths } from "../../../Router/router";
import { useTranslation } from "react-i18next";

export interface AssetHeroRequestCardProps {
  sx?: SxProps;
  assetId: number;
  assetParentTitleId: number | null;
  requestDetails: AdminAssetSingleRequestDetails;
}

const AssetHeroRequestCard: React.FC<AssetHeroRequestCardProps> = (props) => {
  const { t } = useTranslation();
  const { sx = {}, assetId, assetParentTitleId, requestDetails } = props;

  const enqueueSnackbar = useCustomSnackbar();
  const navigate = useNavigate();
  // modal management
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleRemindProvider = () => {
    ingestService.RemindProvider(assetId).then(() => {
      enqueueSnackbar(t("alert:success.remindedProvider"), "success");
    });
  };

  const openModalNewAssignment = () => {
    if (!openModal) return;
    openModal<ModalNewAssignmentElementProps>(ModalNewAssignment, {
      assetId,
      assetProviderId: requestDetails.assignedProviderId!,
    });
  };

  const handleDeleteAsset = (comment?: string) => {
    ingestService.DeleteRequest({ id: assetId, comment: comment || null }).then((response) => {
      if (response.objectId === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID) enqueueSnackbar(response.message!, "primary");
      else {
        if (response.success) {
          enqueueSnackbar(t("alert:success.assetDeletedSuccess", { assetId }), "success");
          if (assetParentTitleId !== null) navigate(`${routePaths.title.path}/${assetParentTitleId}`);
          else navigate(GlobalConst.DEFAULT_REDIRECT_PATH);
        } else enqueueSnackbar(t("alert:error.errorDeletingAsset", { assetId, message: response.message }), "error");
      }
    });
  };

  return (
    <StyledCard variant="outlined" sx={sx}>
      <Stack direction="row" justifyContent="space-between">
        <MainContent justifyContent="space-between" spacing={2}>
          <div>
            <Typography fontWeight={600} variant="subtitle2">
              {t("component:asset.body.requestDetails")}
            </Typography>
          </div>
          <Stack spacing={1}>
            <MetadataListItem title={t("menu:title.requestedBy")}>
              {requestDetails.requestedBy !== "System" ? (
                <Link href="#" underline="hover" fontWeight={500}>
                  {requestDetails.requestedBy}
                </Link>
              ) : (
                requestDetails.requestedBy
              )}
            </MetadataListItem>
            <MetadataListTextItem title={t("menu:title.requestedAt")}>
              {requestDetails.requestedAt}
            </MetadataListTextItem>
            {requestDetails.assignedProviderId && (
              <MetadataListItem title={t("menu:title.providerAssigned")}>
                <Chip size="small" color="info" label={requestDetails.assignedProviderName} />
              </MetadataListItem>
            )}
          </Stack>
        </MainContent>
        <Divider orientation="vertical" sx={{ mr: 5, ml: 5, height: "auto" }} />
        <Actions>
          {requestDetails.assignedProviderId && (
            <Button
              sx={{ mb: 1 }}
              size="small"
              startIcon={<NotificationActiveIcon />}
              variant="contained"
              onClick={handleRemindProvider}
              disabled={!requestDetails.userCanRemindProvider}
            >
              {t("button:remindProvider")}
            </Button>
          )}
          <Button
            sx={{ mb: 1 }}
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={openModalNewAssignment}
            disabled={!requestDetails.userCanAddNewAssignment}
          >
            {t("button:newAssignment")}
          </Button>
          <ModalConfirmActionOpenButton
            displayComment
            modalMessage={t("modal:message.confirmRequestDeletion")}
            onConfirm={(comment?: string) => handleDeleteAsset(comment)}
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={!requestDetails.userCanDeleteRequest}
          >
            {t("button:deleteRequests_one")}
          </ModalConfirmActionOpenButton>
        </Actions>
      </Stack>
    </StyledCard>
  );
};

export default AssetHeroRequestCard;

const StyledCard = styled(Card)(({ theme }) => ({
  padding: `${theme.spacing(3)} ${theme.spacing(5)} ${theme.spacing(3)} ${theme.spacing(3)}`,
  background: "linear-gradient(180deg, rgba(247, 192, 53, 0.05) 0%, rgba(247, 192, 53, 0) 100%)",
}));

const MainContent = styled(Stack)(() => ({
  flex: "1 1 auto",
}));

const Actions = styled(Stack)(() => ({
  width: "240px",
  flex: "0 0 auto",
}));
